import type { Session } from '@supabase/supabase-js';

export function didAuthSessionEnd(
  event: string,
  nextSession: Session | null,
  hadSession: boolean,
): boolean {
  return event === 'SIGNED_OUT' || (!nextSession && event !== 'INITIAL_SESSION' && hadSession);
}

export function didAuthIdentityChange(
  nextSession: Session | null,
  currentSession: Session | null,
): boolean {
  const priorUserId = currentSession?.user.id;
  return Boolean(priorUserId && nextSession && priorUserId !== nextSession.user.id);
}

/**
 * Whether an incoming event only re-states the session already published.
 *
 * auth-js re-notifies `SIGNED_IN` with a freshly deserialized copy of the
 * stored session on every hidden -> visible transition
 * (`GoTrueClient#_recoverAndRefresh`), so switching windows hands the app a new
 * session object carrying identical credentials. Republishing it swaps the
 * identity every consumer keys on for no change in authorization.
 *
 * Only `SIGNED_IN` qualifies, because that is the sole event auth-js delivers
 * redundantly. An allow-list rather than a deny-list of teardown events:
 * `USER_UPDATED` also arrives on unchanged credentials — auth-js re-saves
 * `{...session, user}` and notifies without touching the tokens — so suppressing
 * by credentials alone would strand a stale user in state.
 *
 * Credential equality is byte-for-byte: a refreshed — or MFA-upgraded — token
 * keeps its `session_id` claim, and a caller suppressing work here must see
 * every claim change, `aal` included.
 */
export function isRedundantSessionRepublish(
  event: string,
  nextSession: Session | null,
  publishedSession: Session | null,
  changedAccount: boolean,
): boolean {
  if (event !== 'SIGNED_IN' || changedAccount || !nextSession || !publishedSession) {
    return false;
  }
  return (
    nextSession.user.id === publishedSession.user.id &&
    nextSession.access_token === publishedSession.access_token &&
    nextSession.refresh_token === publishedSession.refresh_token
  );
}
